import React, { useState, useEffect } from 'react';
import { ChatTheme } from '@/types/theme';
import { Message } from '@/types/chat';
import { sanitizeContent, processMessageContent, stripIncompletePlaceholders } from '@/utils/aiUtils';
import { TCPAWarning } from '@/components/chat/TCPAWarning';

/**
 * Props for StreamingMessageBubble component
 * Following OfferChat.ai TypeScript best practices with explicit interfaces
 */
interface StreamingMessageBubbleProps {
  text: string;
  isStreaming: boolean;
  sender: 'user' | 'bot';
  theme?: ChatTheme;
  typingSpeed?: number;
  className?: string;
  timestamp?: Date;
  messageId?: string;
  metadata?: Record<string, any>;
  onActionButtonClick?: (action: { type: 'url' | 'phone'; text: string; value: string }) => void;
}

/**
 * A message bubble component that supports real-time streaming of text
 * Follows OfferChat.ai UI component patterns
 */
export function StreamingMessageBubble({
  text,
  isStreaming,
  sender,
  theme,
  typingSpeed = 400,
  className = '',
  timestamp,
  messageId,
  metadata,
  onActionButtonClick
}: StreamingMessageBubbleProps) {
  const [cursorVisible, setCursorVisible] = useState(true);

  // Animate cursor blinking
  useEffect(() => {
    if (!isStreaming) {
      setCursorVisible(false);
      return;
    }

    // Blink cursor when streaming
    const cursorInterval = setInterval(() => {
      setCursorVisible(prev => !prev);
    }, 500);

    return () => {
      clearInterval(cursorInterval);
    };
  }, [isStreaming]);

  // Determine bubble style based on sender and theme
  const getBubbleStyle = () => {
    const isUser = sender === 'user';
    const overflowWrapValue = sender === 'user' ? 'break-word' : 'anywhere';
    const wordBreakValue = sender === 'user' ? 'normal' : 'break-word';

    return {
      backgroundColor: isUser ?
        theme?.userbubblecolor || '#497CFD' :
        theme?.botbubblecolor || '#F5F5F5',
      color: isUser ?
        theme?.usertextcolor || '#FFFFFF' :
        theme?.bottextcolor || '#000000',
      borderRadius: theme?.borderradius ? `${theme.borderradius}px` : '1rem',
      borderBottomRightRadius: isUser ? '0.25rem' : theme?.borderradius ? `${theme.borderradius}px` : '1rem',
      borderBottomLeftRadius: isUser ? theme?.borderradius ? `${theme.borderradius}px` : '1rem' : '0.25rem',
      padding: '0.75rem 1rem',
      maxWidth: '100%',
      minWidth: '60px',
      width: 'auto',
      wordBreak: wordBreakValue as React.CSSProperties['wordBreak'],
      whiteSpace: 'pre-wrap' as const,
      overflowWrap: overflowWrapValue as React.CSSProperties['overflowWrap'],
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      position: 'relative' as const,
      margin: '0.25rem 0',
      lineHeight: '1.5'
    };
  };

  // Extract question metadata if available
  const questionId = metadata?.questionId;
  const questionType = metadata?.questionType;
  const questionIndex = metadata?.questionIndex;
  const isTcpaWarning = metadata?.isTcpaWarning;

  // Handle TCPA warning messages specially
  if (isTcpaWarning) {
    // Extract the TCPA message content (remove the warning prefix)
    const tcpaContent = text.replace(/^⚠️ \*\*TCPA Notice\*\*\n\n/, '');
    
    return (
      <div
        className={`message-bubble tcpa-warning ${className}`}
        data-testid="tcpa-warning"
        data-message-id={messageId}
        style={{ margin: '0.25rem 0' }}
      >
        <TCPAWarning message={tcpaContent} />
      </div>
    );
  }

  return (
    <div
      className={`message-bubble ${sender}-bubble ${className} ${isStreaming ? 'streaming' : ''}`}
      style={getBubbleStyle()}
      data-testid={`${sender}-message`}
      data-message-id={messageId}
      data-question-id={questionId}
      data-question-type={questionType}
      data-question-index={questionIndex}
      onClick={(event) => {
        if (!onActionButtonClick) return;
        const target = (event.target as HTMLElement | null)?.closest?.('a.chat-action-button, button.chat-action-button') as HTMLElement | null;
        if (!target) return;

        const type = (target.dataset.actionType as 'url' | 'phone') || 'url';
        const text = target.dataset.actionText || target.textContent?.trim() || '';
        const value =
          target.dataset.actionValue ||
          (target instanceof HTMLAnchorElement ? target.getAttribute('href') : '') ||
          '';

        if (text || value) {
          onActionButtonClick({ type, text, value });
        }
      }}
    >
      {/* Process and sanitize content for rich media support */}
      {/* Typing indicator */}
      {text === '...' ? (
          <div
              className="typing-indicator"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: '1rem',
                borderBottomLeftRadius: '0.25rem',
                gap: '0.25rem',
                marginTop: '0.25rem',
                color: theme?.bottextcolor || '#000000',
              }}
              aria-label="Assistant is typing"
          >
            <span className="typing-dot" aria-hidden="true" />
            <span className="typing-dot" aria-hidden="true" />
            <span className="typing-dot" aria-hidden="true" />
          </div>
      ) : (
          <div
              className={`message-content ${sender}-content`}
              style={{
                width: '100%',
                overflowWrap: sender === 'user' ? 'break-word' : 'anywhere',
                wordBreak: sender === 'user' ? 'break-word' : 'break-word',
                whiteSpace: 'pre-wrap',
                hyphens: 'none'
              }}
              dangerouslySetInnerHTML={{
                __html: sanitizeContent(
                  isStreaming
                    ? processMessageContent(
                        stripIncompletePlaceholders(text, {
                          placeholderHtml:
                            '<div class="chat-action-placeholder"><span class="chat-action-spinner" aria-label="Loading action"></span></div>',
                        }),
                        metadata?.rich_content,
                        theme
                      )
                    : processMessageContent(text, metadata?.rich_content, theme)
                )
              }}
          />
      )}

      {/*/!* Typing cursor *!/*/}
      {/*{isStreaming && cursorVisible && (*/}
      {/*  <span*/}
      {/*    className="typing-cursor"*/}
      {/*    aria-hidden="true"*/}
      {/*    style={{*/}
      {/*      display: 'inline-block',*/}
      {/*      width: '2px',*/}
      {/*      height: '1em',*/}
      {/*      backgroundColor: 'currentColor',*/}
      {/*      marginLeft: '2px',*/}
      {/*      verticalAlign: 'middle',*/}
      {/*      animation: 'blink 1s step-end infinite'*/}
      {/*    }}*/}
      {/*  />*/}
      {/*)}*/}
    </div>
  );
}
